import { useState, useCallback, useMemo } from 'react'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  GreenContainer,
  FormContainer,
  FormItemContainer,
  OptionButtonsContainer,
  SubAreasOfPracticeContainer,
  AreasOfPracticeCheckboxContainer,
  AreasOfPracticeCheckboxItem
} from '../../../components/application/defaultComponents'
import { FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import { useApplication } from '../../../context/application'
import { LeftBar } from 'src/components/basic/LeftBar'
import { AddressOptions, ClaimItemFormMainContainer, ClaimItemFormSection, ContainerWithLeftBar, DefaultFormContainer, FlexDiv, FormItemTitle } from 'src/components/application/formComponents/styles'
import StepBullet from 'src/components/basic/StepBullet'
import { AttorneysSharingData, LossPreventionData } from 'src/dtos/applicationData'
import Button from 'src/components/basic/Button'
import produce from 'immer'
import OptionButton from '../../../components/basic/OptionButton'
import flagWithCheckImg from 'src/assets/applicationFormsImgs/flag_with_check.svg'
import StandardInput from '../../../components/basic/StandardInput'
import SharedExpenseAttorneyItemForm from '../../../components/application/formComponents/SharedExpenseAttorneyItemForm'
import StandardCheckbox from '../../../components/basic/StandardCheckbox'
import { conflictOfInterestAvoidance, workScheduling } from 'src/constants/application'
import { ValidationError } from 'yup'
import { generateRandomNumberId } from 'src/utils/math'
import { FirmLossPreventionSchema } from 'src/yup/NewAppSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

interface AttorneysSharingDataWithId extends AttorneysSharingData {
  id: number;
}

const LossPrevention = () => {
  const { application } = useApplication()
  const [lossPrevention, setLossPrevention] = useState<LossPreventionData>(
    () => !!application.firmSection?.lossPrevention ? { ...application.firmSection.lossPrevention } : {}
  )
  const [sharedSpaceAttorneys, setSharedSpaceAttorneys] = useState<AttorneysSharingDataWithId[]>(() => {
    const attorneys = application?.firmSection?.lossPrevention?.sharedSpaceExpensesAttorneys ?? []
    return attorneys.length > 0 ? 
      attorneys.map(att => ({ ...att, id: generateRandomNumberId() })) : 
      [{ id: generateRandomNumberId() }] as AttorneysSharingDataWithId[]
  })
  const [errorStates, setErrorStates] = useState(false)

  const handleAddAttorney = () => {
    setSharedSpaceAttorneys(prev => [...prev, { id: generateRandomNumberId() }])
  }

  const handleRemoveAttorney = (id: number) => {
    setSharedSpaceAttorneys(prev => prev.filter(att => att.id !== id))
  }

  const [selectedScheduling, setSelectedScheduling] = useState<string[]>(() =>
    (!!lossPrevention?.workScheduling) ?
      [...lossPrevention?.workScheduling.filter(ws => !!ws)] :
      []
  )

  const isSchedulingSelected = (ws: string) => selectedScheduling.includes(ws)

  const handleSchedulingSelect = (ws: string) => {
    if (isSchedulingSelected(ws)) {
      setSelectedScheduling(prev => prev.filter(searchWs => searchWs !== ws))
    } else {
      setSelectedScheduling(prev => ([...prev, ws]))
    }
  }

  const [selectedCoi, setSelectedCoi] = useState<string[]>(() =>
  (!!lossPrevention?.avoidConflictOfInterests) ?
    [...lossPrevention?.avoidConflictOfInterests.filter(ws => !!ws)] :
    []
)

const isCoiSelected = (coi: string) => selectedCoi.includes(coi)

const handleCoiSelect = (coi: string) => {
  if (isCoiSelected(coi)) {
    setSelectedCoi(prev => prev.filter(searchCoi => searchCoi !== coi))
  } else {
    setSelectedCoi(prev => ([...prev, coi]))
  }
}

  const hasSuedClientForFees = useMemo(() => {
    if (!!application?.firmSection?.relationships?.hasSuedClientForFees) {
      return {
        isVisible: !!application?.firmSection.relationships?.hasSuedClientForFees,
        value: application?.firmSection.relationships?.hasSuedClientForFees
      }
    }
    return { isVisible: false, value: false }
  }, [application?.firmSection?.relationships?.hasSuedClientForFees])

  const isAllSet = () => {
    return !hasSuedClientForFees.value
  }

  const updateSharedExpenseAttorney = useCallback((index: number, data: Partial<AttorneysSharingData>) => {
    setSharedSpaceAttorneys(produce(draft => { draft[index] = { ...draft[index], ...data } }))
  }, [])

  const updatedApplicationData = () => (
    {
      ...application, 
      currentPage: window.location.pathname,
      firmSection:
      {
        ...application.firmSection,
        lossPrevention: hasSuedClientForFees.value ? { ...lossPrevention, 
          sharedSpaceExpensesAttorneys: sharedSpaceAttorneys.map(att => {
            const { id, ...rest } = att
            return rest
          }), 
          workScheduling: selectedScheduling,
          avoidConflictOfInterests: selectedCoi
        } : undefined,
        relationships: {
          ...application.firmSection?.relationships,
          hasSuedClientForFees: hasSuedClientForFees.value
        }
      }
    })

  const goNextRoute = () => {
    return '/attorneys/main'
  }

  const goBackRoute = () => {
    return '/firm/relationships'
  }
  
  const validatePage = useCallback(() => {
    try {
      FirmLossPreventionSchema.validateSync({
        ...lossPrevention,
        workScheduling: selectedScheduling,
        avoidConflictOfInterests: selectedCoi,
        sharedSpaceExpensesAttorneys: sharedSpaceAttorneys
      }, { abortEarly: false })
      return []
    } catch(err) {
      const { errors } = err as ValidationError
      return errors
    }
  }, [lossPrevention, selectedCoi, selectedScheduling, sharedSpaceAttorneys])

  const pageValidation = useMemo(() => {
    const incorrectSections = validatePage()

    let steps: {[key: string]: boolean} = {}
    
    for (let stepNumber = 1; stepNumber <= 10; stepNumber++) {
      steps[`step${stepNumber}`] = !incorrectSections.includes(`step${stepNumber}`)
    }

    return steps
  }, [validatePage])

  const handleScrollToFirstErrorState = () => {
    const incorrectSections = validatePage()
    if(incorrectSections.length > 0) {
      document.getElementById(incorrectSections[0])?.scrollIntoView({ behavior: 'smooth' })
      setErrorStates(true)
    }
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='80%'>
        <DefaultTitle>Please fill out the following regarding Loss Prevention</DefaultTitle>
        {isAllSet() ? (
          <GreenContainer>
            <div className='claim-all-set'>
              <div className='claim-all-set-icon'>
                <img src={flagWithCheckImg} alt="All Set" />
              </div>
              <div className='claim-all-set-message'>
                <h3>All Set!</h3>
                <h4>You no longer need to complete this section.</h4>
              </div>
            </div>
          </GreenContainer>
        ) :
          <FormContainer maxWidth='760px' style={{ marginTop: '90px' }}>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step1" isComplete={pageValidation.step1}>1</StepBullet>
              <DefaultFormContainer>
                <FormItemTitle>Does Applicant share office spaces or expenses with another attorney or law firm?</FormItemTitle>
                <AddressOptions>
                  <OptionButton
                    checked={lossPrevention.shareOfficeSpaceOrExpenses === true}
                    onChange={() => setLossPrevention({ ...lossPrevention, shareOfficeSpaceOrExpenses: true })}
                    isIncorrect={!pageValidation.step1 && errorStates && lossPrevention.shareOfficeSpaceOrExpenses === undefined}
                    errorCode='EC211'
                  >
                    Yes
                  </OptionButton>
                  <OptionButton
                    checked={lossPrevention.shareOfficeSpaceOrExpenses === false}
                    onChange={() => setLossPrevention({ ...lossPrevention, shareOfficeSpaceOrExpenses: false })}
                    isIncorrect={!pageValidation.step1 && errorStates && lossPrevention.shareOfficeSpaceOrExpenses === undefined}
                    errorCode='EC211'
                  >
                    No
                  </OptionButton>
                </AddressOptions>
                <ValidationErrorMessage errorCode='EC211' />
                {!pageValidation.step1 && errorStates && lossPrevention.shareOfficeSpaceOrExpenses === undefined ? (
                  <FormInputErrorMessage>
                    This is a required field
                  </FormInputErrorMessage>
                ) : null}
                {(lossPrevention.shareOfficeSpaceOrExpenses === true) ? (
                  <>

                    {sharedSpaceAttorneys.map((att, index) => (
                      <ContainerWithLeftBar key={att.id} maxWidth='490px'>
                        <LeftBar />
                        <FlexDiv dir='column'>
                          <SharedExpenseAttorneyItemForm
                            sharedExpenseAttorney={att}
                            itemIndex={index}
                            setSharedExpenseAttorney={data => updateSharedExpenseAttorney(index, data)}
                            isIncorrect={!pageValidation.step1 && errorStates}
                            errorCode='EC212'
                          />
                          {index > 0 ? (
                            <FlexDiv margin='24px 0 0 24px'>
                              <Button 
                                variant='error'
                                onClick={() => handleRemoveAttorney(att.id)}
                              >
                                Remove Office Sharing Arrangement
                              </Button>
                            </FlexDiv>
                          ) : null}
                        </FlexDiv>
                      </ContainerWithLeftBar>
                    ))}

                    <Button
                      variant="primary"
                      onClick={handleAddAttorney}
                      style={{ marginTop: '60px', maxWidth: '360px', whiteSpace: 'normal' }}
                    >
                      Add another attorney or law firm
                    </Button>
                  </>
                ) : null}
              </DefaultFormContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step2" isComplete={pageValidation.step2}>2</StepBullet>
              <StandardInput
                label="If Applicant is a sole practitioner, who is responsible for Applicant's practice in the event of a prolonged absence? Leave blank if not applicable"
                labelSize='high'
                placeholder='None'
                value={lossPrevention.prolongedAbsenceResponsible ?? ''}
                onChange={({ target: { value } }) => setLossPrevention(prev => ({ ...prev, prolongedAbsenceResponsible: value }))}
                width='50%'
              />
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step3" isComplete={pageValidation.step3}>3</StepBullet>
              <DefaultFormContainer>
                <FormItemTitle>How does Applicant handle the scheduling of work, deadlines and appointments?</FormItemTitle>
                <br />
                <AreasOfPracticeCheckboxContainer>
                  {workScheduling.map((handling, index) => (
                    <AreasOfPracticeCheckboxItem key={index}>
                      <StandardCheckbox
                        variation="medium"
                        label={handling}
                        withBg
                        onChange={() => handleSchedulingSelect(handling)}
                        checked={isSchedulingSelected(handling)}
                        isIncorrect={!pageValidation.step3 && errorStates && selectedScheduling.length === 0}
                      />
                      {(handling === 'Other' && isSchedulingSelected(handling)) ? (
                        <SubAreasOfPracticeContainer>
                          <LeftBar />
                          <div className="subareas-container">
                            <StandardInput
                              label='Please describe'
                              onChange={e => setLossPrevention({...lossPrevention, workSchedulingOther: e.target.value})}
                              value={lossPrevention.workSchedulingOther}
                              isIncorrect={!pageValidation.step3 && errorStates && !lossPrevention?.workSchedulingOther}
                              errorMessage={!pageValidation.step3 && errorStates && !lossPrevention?.workSchedulingOther ? 'This is a required field' : ''}
                            />
                          </div>
                        </SubAreasOfPracticeContainer>
                      ) : null}

                    </AreasOfPracticeCheckboxItem>
                  ))}
                </AreasOfPracticeCheckboxContainer>
              </DefaultFormContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step4" isComplete={pageValidation.step4}>4</StepBullet>
              <OptionButtonsContainer labelSize='big'>
                <span>Do all firm members use engagement letters that specify scope of services, terms and billing rates?</span>
                <div>
                  <OptionButton
                    checked={lossPrevention?.engagementLetters === true}
                    onChange={() => setLossPrevention({ ...lossPrevention, engagementLetters: true })}
                    isIncorrect={!pageValidation.step4 && errorStates}
                    errorCode='EC213'
                  >
                    Yes
                  </OptionButton>
                  <OptionButton
                    checked={lossPrevention?.engagementLetters === false}
                    onChange={() => setLossPrevention({ ...lossPrevention, engagementLetters: false })}
                    isIncorrect={!pageValidation.step4 && errorStates}
                    errorCode='EC213'
                  >
                    No
                  </OptionButton>
                </div>
                <ValidationErrorMessage errorCode='EC213' />
                {!pageValidation.step4 && errorStates ? (
                  <FormInputErrorMessage>
                    This is a required field
                  </FormInputErrorMessage>
                ) : null}
              </OptionButtonsContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step5" isComplete={pageValidation.step5}>5</StepBullet>
              <OptionButtonsContainer labelSize='big'>
                <span>Do all firm members use non-engagement letter when declining a representation?</span>
                <div>
                  <OptionButton
                    checked={lossPrevention?.nonEngagementLettersWhenDeclining === true}
                    onChange={() => setLossPrevention({ ...lossPrevention, nonEngagementLettersWhenDeclining: true })}
                    isIncorrect={!pageValidation.step5 && errorStates}
                    errorCode='EC214'
                  >
                    Yes
                  </OptionButton>
                  <OptionButton
                    checked={lossPrevention?.nonEngagementLettersWhenDeclining === false}
                    onChange={() => setLossPrevention({ ...lossPrevention, nonEngagementLettersWhenDeclining: false })}
                    isIncorrect={!pageValidation.step5 && errorStates}
                    errorCode='EC214'
                  >
                    No
                  </OptionButton>
                </div>
                <ValidationErrorMessage errorCode='EC214' />
                {!pageValidation.step5 && errorStates ? (
                  <FormInputErrorMessage>
                    This is a required field
                  </FormInputErrorMessage>
                ) : null}
              </OptionButtonsContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step6" isComplete={pageValidation.step6}>6</StepBullet>
              <OptionButtonsContainer labelSize='big'>
                <span>Do all firm members use disengagement letter when ceasing representation?</span>
                <div>
                  <OptionButton
                    checked={lossPrevention?.disengagementLettersWhenCeasing === true}
                    onChange={() => setLossPrevention({ ...lossPrevention, disengagementLettersWhenCeasing: true })}
                    isIncorrect={!pageValidation.step6 && errorStates}
                    errorCode='EC215'
                  >
                    Yes
                  </OptionButton>
                  <OptionButton
                    checked={lossPrevention?.disengagementLettersWhenCeasing === false}
                    onChange={() => setLossPrevention({ ...lossPrevention, disengagementLettersWhenCeasing: false })}
                    isIncorrect={!pageValidation.step6 && errorStates}
                    errorCode='EC215'
                  >
                    No
                  </OptionButton>
                </div>
                <ValidationErrorMessage errorCode='EC215' />
                {!pageValidation.step6 && errorStates ? (
                  <FormInputErrorMessage>
                    This is a required field
                  </FormInputErrorMessage>
                ) : null}
              </OptionButtonsContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step7" isComplete={pageValidation.step7}>7</StepBullet>
              <DefaultFormContainer>
                <FormItemTitle>How does Applicant avoid conflicts of interest among and with clients? (Attach any written policies/procedures)</FormItemTitle>
                <br />
                <AreasOfPracticeCheckboxContainer>
                  {conflictOfInterestAvoidance.map((coi, index) => (
                    <AreasOfPracticeCheckboxItem key={index}>
                      <StandardCheckbox
                        variation="medium"
                        label={coi}
                        withBg
                        onChange={() => handleCoiSelect(coi)}
                        checked={isCoiSelected(coi)}
                        isIncorrect={!pageValidation.step7 && errorStates && selectedCoi.length === 0}
                      />
                      {(coi === 'Other' && isCoiSelected(coi)) ? (
                        <SubAreasOfPracticeContainer>
                          <LeftBar />
                          <div className="subareas-container">
                            <StandardInput
                              label='Please describe'
                              onChange={e => setLossPrevention({...lossPrevention, avoidConflictOfInterestsOther: e.target.value})}
                              value={lossPrevention.avoidConflictOfInterestsOther}
                              isIncorrect={!pageValidation.step7 && errorStates && !lossPrevention?.avoidConflictOfInterestsOther}
                              errorMessage={!pageValidation.step7 && errorStates && !lossPrevention?.avoidConflictOfInterestsOther ? 'This is a required field' : ''}
                            />
                          </div>
                        </SubAreasOfPracticeContainer>
                      ) : null}

                    </AreasOfPracticeCheckboxItem>
                  ))}
                </AreasOfPracticeCheckboxContainer>
              </DefaultFormContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step8" isComplete={pageValidation.step8}>8</StepBullet>
              <OptionButtonsContainer labelSize='big'>
                <span>If a potential conflict of interest exists, do all firm members obtain written conflicts waivers that clearly:</span>
                <ContainerWithLeftBar>
                  <LeftBar />
                  <FlexDiv gap="18px" margin="0px 0px 0px 24px">
                    <ClaimItemFormMainContainer>
                      <ClaimItemFormSection maxWidth='80%'>
                        <OptionButtonsContainer>
                          <span>Advise client(s) of the nature of potential conflict?</span>
                          <div>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversPotentialConflict === true}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversPotentialConflict: true })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversPotentialConflict === undefined}
                              errorCode='EC216'
                            >
                              Yes
                            </OptionButton>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversPotentialConflict === false}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversPotentialConflict: false })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversPotentialConflict === undefined}
                              errorCode='EC216'
                            >
                              No
                            </OptionButton>
                          </div>
                          <ValidationErrorMessage errorCode='EC216' />
                          {!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversPotentialConflict === undefined ? (
                            <FormInputErrorMessage>
                              This is a required field
                            </FormInputErrorMessage>
                          ) : null}
                        </OptionButtonsContainer>
                      </ClaimItemFormSection>
                      <ClaimItemFormSection maxWidth='80%'>
                        <OptionButtonsContainer>
                          <span>Advise client(s) how the potential conflict could affect representation?</span>
                          <div>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversRepresentationAffected === true}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversRepresentationAffected: true })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversRepresentationAffected === undefined}
                              errorCode='EC217'
                            >
                              Yes
                            </OptionButton>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversRepresentationAffected === false}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversRepresentationAffected: false })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversRepresentationAffected === undefined}
                              errorCode='EC217'
                            >
                              No
                            </OptionButton>
                          </div>
                          <ValidationErrorMessage errorCode='EC217' />
                          {!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversRepresentationAffected === undefined ? (
                            <FormInputErrorMessage>
                              This is a required field
                            </FormInputErrorMessage>
                          ) : null}
                        </OptionButtonsContainer>
                      </ClaimItemFormSection>
                      <ClaimItemFormSection maxWidth='80%'>
                        <OptionButtonsContainer>
                          <span>Advise client(s) to consult another attorney about the potential conflict?</span>
                          <div>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversAnotherAttorneyConsult === true}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversAnotherAttorneyConsult: true })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversAnotherAttorneyConsult === undefined}
                              errorCode='EC218'
                            >
                              Yes
                            </OptionButton>
                            <OptionButton
                              checked={lossPrevention?.conflictWaiversAnotherAttorneyConsult === false}
                              onChange={() => setLossPrevention({ ...lossPrevention, conflictWaiversAnotherAttorneyConsult: false })}
                              isIncorrect={!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversAnotherAttorneyConsult === undefined}
                              errorCode='EC218'
                            >
                              No
                            </OptionButton>
                          </div>
                          <ValidationErrorMessage errorCode='EC218' />
                          {!pageValidation.step8 && errorStates && lossPrevention?.conflictWaiversAnotherAttorneyConsult === undefined ? (
                            <FormInputErrorMessage>
                              This is a required field
                            </FormInputErrorMessage>
                          ) : null}
                        </OptionButtonsContainer>
                      </ClaimItemFormSection>
                    </ClaimItemFormMainContainer>
                  </FlexDiv>
                </ContainerWithLeftBar>
              </OptionButtonsContainer>
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step9" isComplete={pageValidation.step9}>9</StepBullet>
              <StandardInput
                label="How many times in the last two years has Applicant sued a client for fees?"
                labelSize='high'
                type='number'
                value={lossPrevention.timesSuedClientsForFees ?? ''}
                onChange={({ target: { value } }) => setLossPrevention(prev => ({ ...prev, timesSuedClientsForFees: (isNaN(Number(value)) || !value) ? undefined : Number(value) }))}
                width='50%'
                isIncorrect={!pageValidation.step9 && errorStates}
                errorMessage={!pageValidation.step9 && errorStates ? 'This is a required field' : ''}
                errorCode='EC219'
              />
            </FormItemContainer>
            <FormItemContainer maxWidth='100%'>
              <StepBullet id="step10" isComplete={pageValidation.step10}>10</StepBullet>
              <StandardInput
                label="How many clients that were sued, if any, filed a counterclaim for legal malpractice or breach of fiduciary duty?"
                labelSize='high'
                type='number'
                value={lossPrevention.howManyClientsSuedFiledCounterClaim ?? ''}
                onChange={({ target: { value } }) => setLossPrevention(prev => ({ ...prev, howManyClientsSuedFiledCounterClaim: (isNaN(Number(value)) || !value) ? undefined : Number(value) }))}
                width='50%'
                isIncorrect={!pageValidation.step10 && errorStates}
                errorMessage={!pageValidation.step10 && errorStates ? 'This is a required field' : ''}
                errorCode='EC220'
              />
            </FormItemContainer>
          </FormContainer>
        }
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute={goBackRoute()}
        isFormValid={Object.keys(pageValidation).every(key => pageValidation[key] === true) || isAllSet()}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollToFirstErrorState}
      />
    </ApplicationBodyContainer>
  )
}

export default LossPrevention